/**
 *  @class
 *  @function DeferredAppBlocks
 *  Renders app blocks kept in a <template> once the page has loaded, or earlier when the
 *  visitor scrolls near them, so app scripts and images don't compete with the first view.
 */
if (!customElements.get('deferred-app-blocks')) {
  // Judge.me's loader scans for widgets once and then guards itself with jdgm._loaderExecuted.
  // Re-run it so widgets added after that scan initialise. Relies on Judge.me internals: if a Judge.me
  // update breaks this, turn off "Load app blocks after page load" on the Apps section.
  const rerunJudgeMeLoader = (container) => {
    if (!container.querySelector('[class*="jdgm-"]')) return;
    if (!window.jdgm || !window.jdgm._loaderExecuted) return;

    const loader = document.querySelector('script[src*="judgeme"][src*="/assets/loader.js"]');
    if (!loader) return;

    window.jdgm._loaderExecuted = false;
    const script = document.createElement('script');
    script.src = loader.src;
    document.body.appendChild(script);
  };

  class DeferredAppBlocks extends HTMLElement {
    constructor() {
      super();
      this.render = this.render.bind(this);
      this.watch = this.watch.bind(this);
    }

    connectedCallback() {
      // App loaders (Judge.me included) scan on DOMContentLoaded; start after that so a re-run never overlaps their first scan
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.watch, { once: true });
        return;
      }

      this.watch();
    }

    watch() {
      if (!this.isConnected) return;

      if (document.readyState === 'complete') {
        this.render();
        return;
      }

      window.addEventListener('load', this.render, { once: true });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.render();
        }, { rootMargin: '800px 0px' });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.watch);
      window.removeEventListener('load', this.render);
      this.observer?.disconnect();
    }

    render() {
      const template = this.querySelector(':scope > template');
      if (!template) return;

      window.removeEventListener('load', this.render);
      this.observer?.disconnect();

      // Inserting a clone of template content (unlike innerHTML) runs the app blocks' inline scripts
      template.replaceWith(template.content.cloneNode(true));
      rerunJudgeMeLoader(this);
    }
  }

  customElements.define('deferred-app-blocks', DeferredAppBlocks);
}
